import type { Agency, Merchant, MerchantStore, RoleOption, User } from '../../../common/types';
import {
  useGetUsersForAgencyQuery,
  useGetUsersForMerchantQuery,
  useGetUsersForStoreQuery,
  useGetUsersQuery
} from '../user.api-slice';
import AddUser from '../components/AddUser';
import Breadcrumb from '../../../components/Breadcrumb';
import DeleteUser from '../components/DeleteUser';
import EditUser from '../components/EditUser';
import { getAgencyMerchantStoreBasedOnScopeAndRole } from '../../../common/helpers';
import { Link } from 'react-router-dom';
import ListUsers from '../components/ListUsers';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Pagination from '../../../components/Pagination';
import { skipToken } from '@reduxjs/toolkit/query';
import SuccessMessage from '../../../components/success-message';
import { useAuth } from '../../../app/hooks/useAuth';
import { UserRole } from '../../../common/constants';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  scope?: 'all' | 'agency' | 'merchant' | 'merchantStore';
  agency?: Agency;
  merchant?: Merchant;
  store?: MerchantStore;
  filter?: { pending: boolean };
}

const getPageTitle = ({ scope, filter, agency, merchant, store }: IProps): string => {
  const commonPageTitlePart = 'List of';
  const pageTitle =
    scope === 'agency' && agency != null
      ? `${commonPageTitlePart} Users for Agency ${agency.name}`
      : scope === 'merchant' && merchant != null
      ? `${commonPageTitlePart} Users for Merchant ${merchant.name}`
      : scope === 'merchantStore' && store != null
      ? `${commonPageTitlePart} Users for Store ${store.name}`
      : filter?.pending === true
      ? ' Pending Users'
      : ' Users';
  return pageTitle;
};

const getAvailableRoles = (scope: string, loggedInUserRole?: string): RoleOption[] => {
  const roles: RoleOption[] = [];
  if (UserRole.SUPER_ADMIN === loggedInUserRole) {
    if (scope === 'all') {
      roles.push(
        { value: UserRole.SUPER_ADMIN, label: 'Super Admin' },
        { value: UserRole.AGENCY_ADMIN, label: 'Agency Admin' },
        { value: UserRole.MERCHANT_ADMIN, label: 'Merchant Admin' },
        { value: UserRole.STORE_ADMIN, label: 'Store Admin' },
        { value: UserRole.EMPLOYEE, label: 'Employee' }
      );
    } else if (scope === 'agency') {
      roles.push(
        { value: UserRole.AGENCY_ADMIN, label: 'Agency Admin' },
        { value: UserRole.MERCHANT_ADMIN, label: 'Merchant Admin' },
        { value: UserRole.STORE_ADMIN, label: 'Store Admin' },
        { value: UserRole.EMPLOYEE, label: 'Employee' }
      );
    } else if (scope === 'merchant') {
      roles.push(
        { value: UserRole.MERCHANT_ADMIN, label: 'Merchant Admin' },
        { value: UserRole.STORE_ADMIN, label: 'Store Admin' },
        { value: UserRole.EMPLOYEE, label: 'Employee' }
      );
    } else if (scope === 'store') {
      roles.push(
        { value: UserRole.STORE_ADMIN, label: 'Store Admin' },
        { value: UserRole.EMPLOYEE, label: 'Employee' }
      );
    } else {
      roles.push({ value: UserRole.EMPLOYEE, label: 'Employee' });
    }
  } else if (UserRole.AGENCY_ADMIN === loggedInUserRole) {
    if (scope === 'all' || scope === 'merchant') {
      roles.push(
        { value: UserRole.MERCHANT_ADMIN, label: 'Merchant Admin' },
        { value: UserRole.STORE_ADMIN, label: 'Store Admin' },
        { value: UserRole.EMPLOYEE, label: 'Employee' }
      );
    } else if (scope === 'store') {
      roles.push(
        { value: UserRole.STORE_ADMIN, label: 'Store Admin' },
        { value: UserRole.EMPLOYEE, label: 'Employee' }
      );
    } else {
      roles.push({ value: UserRole.EMPLOYEE, label: 'Employee' });
    }
  } else if (UserRole.MERCHANT_ADMIN === loggedInUserRole) {
    if (scope === 'all' || scope === 'store') {
      roles.push(
        { value: UserRole.STORE_ADMIN, label: 'Store Admin' },
        { value: UserRole.EMPLOYEE, label: 'Employee' }
      );
    } else {
      roles.push({ value: UserRole.EMPLOYEE, label: 'Employee' });
    }
  }

  return roles;
};

const ListUsersPage: React.FC<IProps> = ({ scope = 'all', filter, agency, merchant, store }) => {
  const [editingItem, setEditingItem] = useState<User | null>(null);
  const [deletingItem, setDeletingItem] = useState<User | null>(null);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const { t } = useTranslation();
  const { user } = useAuth();
  const injectedIds = getAgencyMerchantStoreBasedOnScopeAndRole({ loggedInUser: user, scope, agency, merchant, store });

  const { data: users, isLoading } =
    scope === 'agency'
      ? useGetUsersForAgencyQuery(agency != null ? { page, id: agency.id } : skipToken)
      : scope === 'merchant'
      ? useGetUsersForMerchantQuery(merchant != null ? { page, id: merchant.id } : skipToken)
      : scope === 'merchantStore'
      ? useGetUsersForStoreQuery(store != null ? { page, id: store.id } : skipToken)
      : useGetUsersQuery({ page, pendingOnly: filter?.pending === true });

  const toggleAddModal = (): void => {
    setOpenAddModal(!openAddModal);
  };

  const toggleEditModal = (): void => {
    if (openEditModal) {
      setEditingItem(null);
    }
    setOpenEditModal(!openEditModal);
  };

  const toggleDeleteModal = (): void => {
    setOpenDeleteModal(!openDeleteModal);
  };

  const handleEditingItem = (item: User | null): void => {
    setEditingItem(item);
  };

  const handleDeletingItem = (item: User | null): void => {
    setDeletingItem(item);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="pb-4">
        <h1>{t('users.list.title')}</h1>
        <Breadcrumb
          items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('merchants.list.breadcrumbLabel') }]}
        />
      </div>
      <div className="w-full flex justify-between items-center">
        <h1>{getPageTitle({ scope, filter, agency, merchant, store })}</h1>
        <div>
          {scope === 'all' && filter?.pending !== true && (
            <Link to={`/admin/users/pending-requests`}>
              <button className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">Pending Users</button>
            </Link>
          )}
          {user?.role !== UserRole.EMPLOYEE && filter?.pending !== true && (
            <button onClick={toggleAddModal} className="text-white ml-4 px-5 py-2 rounded-lg bg-[#1D8A43]">
              Invite User
            </button>
          )}
        </div>

        {filter?.pending === true && (
          <div>
            <Link to={`/admin/users`}>
              <button className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">List Users</button>
            </Link>
          </div>
        )}
      </div>

      <AddUser
        isOpen={openAddModal}
        toggleModal={toggleAddModal}
        availableRoles={getAvailableRoles(scope, user?.role)}
        injectedIds={injectedIds}
      />

      <EditUser isOpen={openEditModal} toggleEditModal={toggleEditModal} item={editingItem} />

      <DeleteUser isOpen={openDeleteModal} toggleDeleteModal={toggleDeleteModal} item={deletingItem} />
      {users?.data == null ||
        (users.data.length === 0 && <SuccessMessage message={t('users.list.empty')} type="error" />)}
      {users?.data != null && users?.data.length > 0 && (
        <>
          <ListUsers
            items={users.data}
            filter={{ pending: filter?.pending === true }}
            toggleEditModal={toggleEditModal}
            toggleDeleteModal={toggleDeleteModal}
            handleEditingItem={handleEditingItem}
            handleDeletingItem={handleDeletingItem}
          />
          <Pagination pageData={users.meta} handlePageChange={setPage} />
        </>
      )}
    </div>
  );
};

export default ListUsersPage;
